import { routePath } from "config/routeMgt/routePath";
import { useSignOut } from "react-auth-kit";
import { NavLink, useNavigate } from "react-router-dom";
import homeIcon from "assets/icons/dashboard.png";
import jobsIcon from "assets/icons/jobs.png";
import { useEffect, useState } from "react";
import settingsIcon from "assets/icons/settings.png";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { Progress, Modal } from "antd";
import { AppButton } from "components/button/AppButton";
import avatar from "assets/images/default.webp";
import { useGetTalentCount } from "features/home/hooks/useGetTalentCount";
import helpIcon from "assets/icons/support.png";
import { MessageOutlined } from "@ant-design/icons";
import { SupportChat } from "features/support/pages/SupportChat";
import { useGetUnreadMessageCount } from "features/message/hooks/useGetUnreadMessageCount";

interface TalentSidebarProps {
  startTour: () => void;
}

export const TalentSidebar = ({ startTour }: TalentSidebarProps) => {
  const signOut = useSignOut();
  const [isLogoutModalVisible, setLogoutModalVisible] = useState(false);
  const { userInfo } = useGetUserInfo();
  const { data } = useGetTalentCount();
  const { data: unreadMessageCount = 0 } = useGetUnreadMessageCount();
  const [loading, setLoading] = useState(true);
  const [isChatOpen, setIsChatOpen] = useState(false);

  useEffect(() => {
    if (data) {
      setLoading(false);
    }
  }, [data]);
  const navigate = useNavigate();

  const handleLogout = () => {
    signOut();
    setLogoutModalVisible(false);
  };

  return (
    <div className="flex flex-col justify-between h-screen">
      <div className="flex flex-col mt-3 gap-y-3 justify-center px-5">
        <NavLink
          to={`${routePath.home}?skipRedirect=true`}
          className="sidebarLink"
          data-tour="talent-dashboard"
        >
          <img src={homeIcon} alt="Home Icon" className="h-6" />
          <span className="text-sm">Dashboard</span>
        </NavLink>
        <NavLink
          to={routePath.jobs}
          className="sidebarLink"
          data-tour="talent-jobs"
        >
          <img src={jobsIcon} alt="Job Icon" className="h-6" />
          <span className="text-sm ">Jobs</span>
        </NavLink>
        <NavLink
          to={routePath.invites}
          className="sidebarLink"
          data-tour="talent-invites"
        >
          <i className="ri-checkbox-line text-xl"></i>
          <span className="text-sm ">Invites</span>
        </NavLink>

        <NavLink to={routePath.settings} className="sidebarLink">
          <img src={settingsIcon} alt="My Profile Icon" className="h-6" />
          <span className="text-sm">My Profile</span>
        </NavLink>

        <NavLink to={routePath.messages} className="sidebarLink relative">
          <img src={jobsIcon} alt="message Icon" className="h-6" />
          <span className="text-sm flex items-center gap-6">
            Messages
            {unreadMessageCount > 0 && (
              <span className="bg-red-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5 min-w-[18px] h-[18px] flex items-center justify-center">
                {unreadMessageCount > 99 ? "99+" : unreadMessageCount}
              </span>
            )}
          </span>
        </NavLink>

        <div
          className="fixed bottom-3 right-28 w-14 h-14 bg-primary rounded-full flex items-center justify-center shadow-lg cursor-pointer z-50"
          onClick={() => setIsChatOpen(true)}
        >
          <MessageOutlined className="text-white text-xl" />
        </div>

        {isChatOpen && (
          <SupportChat isOpen={isChatOpen} setIsOpen={setIsChatOpen} />
        )}
      </div>

      <div className="flex flex-col mt-3 gap-y-3 justify-center px-5">
        <div className="sidebarLink cursor-pointer" onClick={startTour}>
          <img src={helpIcon} alt="Help Icon" className="h-6" />
          <span className="text-sm">Tour Guide</span>
        </div>
      </div>

      {!loading && data?.profile.total !== 100 && (
        <div className="mx-2 mb-[80px]">
          <div className="bg-white mx-auto px-2 py-4 rounded-lg">
            <img
              src={userInfo?.photo != null ? userInfo?.photo : avatar}
              alt="avatar"
              className="h-12 w-12 rounded-full mb-2"
            />
            <div className="flex flex-row items-center">
              <h1 className="text-xs font-bold mb-4">Complete your Profile!</h1>
              <Progress
                type="circle"
                strokeColor={
                  data?.profile.total === 100
                    ? "#65DE02"
                    : data?.profile.total >= 90
                      ? "#3CB371"
                      : data?.profile.total >= 80
                        ? "#FFBF00"
                        : data?.profile.total >= 70
                          ? "#FF8C00"
                          : data?.profile.total >= 60
                            ? "#FF6347"
                            : "#FF0000"
                }
                percent={data?.profile.total}
                strokeWidth={10}
                size={55}
                format={() => null}
              />
            </div>

            <p className="text-gray-700 mb-8 text-xs">
              Complete your registration to verify your account.
            </p>
            <AppButton
              type="button"
              label="Complete Now"
              handleClick={() => navigate("/settings")}
            />
          </div>
        </div>
      )}

      {isLogoutModalVisible && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="p-6 text-center relative">
              <button
                onClick={() => setLogoutModalVisible(false)}
                className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>

              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="text-red-500 text-2xl">⚠️</div>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Sign out
              </h3>
              <p className="text-gray-600 mb-6">
                Are you sure you would like to sign out of your Arno account?
              </p>

              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => setLogoutModalVisible(false)}
                  className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  No
                </button>
                <button
                  onClick={handleLogout}
                  className="px-6 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600"
                >
                  Yes, Sign Out
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
